/// <amd-module name="Test/Analytics/TestInsightsHistoryList/Stores/TestInsightsTestHistoryListStore" />
import * as CommonTypes from "Test/Analytics/Common/Types";
import { ConfigurationUtility } from "Test/Analytics/Common/Utils/ConfigurationUtility";
import { StringFormattingUtility } from "Test/Analytics/Common/Utils/StringFormattingUtility";
import { TestInsightsTestHistoryListActions } from "Test/Analytics/TestInsightsHistoryList/Actions/TestInsightsTestHistoryListActions";
import * as Resources from "Test/Analytics/TestInsightsHistoryList/Resources";
import { ITestHistoryListData, ITestInsightsTestHistoryListItem, ITestResultIdentifier } from "Test/Analytics/TestInsightsHistoryList/Types";
import { DurationFormatter } from "Test/Analytics/TestResultsGridCommon/DurationFormatter";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import { FilterMetadataActions } from "Test/MetadataHandler/Actions/FilterMetadataActions";
import { Store } from "VSS/Reflux/Store";

export interface ITestInsightsTestHistoryListState {
    items: ITestInsightsTestHistoryListItem[] | null;
}

export class TestInsightsTestHistoryListStore extends Store {
    private _state: ITestInsightsTestHistoryListState;
    private _confValues: CommonTypes.IReportConfiguration;
    private _itemList: ITestHistoryListData;
    private _showMoreKey: ITestResultIdentifier;
    private _stageIdToNameMap: { [key: string]: string };

    constructor(
        private _actions: TestInsightsTestHistoryListActions,
        private _filterMetadataActions: FilterMetadataActions,
        private _contextType: TCMContracts.TestResultsContextType
    ) {
        super("");
        this._initialize();
    }

    public getState(): ITestInsightsTestHistoryListState {
        return this._state;
    }

    public getNextPageToken(): CommonTypes.INextDataPageToken | undefined {
        return this._itemList.nextPageToken;
    }

    public getConfValues(): CommonTypes.IReportConfiguration {
        return this._confValues;
    }

    public dispose(): void {
        this._actions.updateConfigurationValuesAction.removeListener(this._onUpdateConfiguration);
        this._actions.updateTestHistoryListAction.removeListener(this._onUpdateTestHistoryList);
        this._actions.appendTestHistoryListAction.removeListener(this._onAppendTestHistoryList);
        this._filterMetadataActions.updateStageIdToNameMapAction.removeListener(this._onUpdateStageIdToNameMap);
    }

    private _initialize(): void {
        this._itemList = { testHistoryListItems: [] } as ITestHistoryListData;
        this._state = {} as ITestInsightsTestHistoryListState;

        this._actions.updateConfigurationValuesAction.addListener(this._onUpdateConfiguration);
        this._actions.updateTestHistoryListAction.addListener(this._onUpdateTestHistoryList);
        this._actions.appendTestHistoryListAction.addListener(this._onAppendTestHistoryList);
        this._filterMetadataActions.updateStageIdToNameMapAction.addListener(this._onUpdateStageIdToNameMap);

        this._showMoreKey = { testRunId: 0, testResultId: 0 } as ITestResultIdentifier;
    }

    private _onUpdateConfiguration = (confValues: CommonTypes.IReportConfiguration) => {
        this._confValues = confValues;
        this._state.items = null; // This is done since after config change it will take time for data to come and component can show loading experience till then.
        this._itemList = { testHistoryListItems: [] } as ITestHistoryListData;
        this.emitChanged();
    };

    private _onUpdateTestHistoryList = (testHistoryListData: ITestHistoryListData) => {
        if (this._confValues && !ConfigurationUtility.areConfValuesEqual(this._confValues, testHistoryListData.confValues)) {
            // Do not update store as the configuration has changed.
            return;
        }

        this._itemList = { testHistoryListItems: [] } as ITestHistoryListData;
        this._itemList.testHistoryListItems.push(...(testHistoryListData.testHistoryListItems || []));
        this._itemList.nextPageToken = testHistoryListData.nextPageToken;

        if (!(this._contextType === TCMContracts.TestResultsContextType.Release) || this._stageIdToNameMap) {
            this._createItemListToShow();
            this.emitChanged();
        }
    };

    private _onAppendTestHistoryList = (testHistoryListData: ITestHistoryListData) => {
        if (this._confValues && !ConfigurationUtility.areConfValuesEqual(this._confValues, testHistoryListData.confValues)) {
            // Do not update store as the configuration has changed.
            return;
        }

        this._itemList.testHistoryListItems.push(...(testHistoryListData.testHistoryListItems || []));
        this._itemList.nextPageToken = testHistoryListData.nextPageToken;
        this._createItemListToShow();
        this.emitChanged();
    };

    private _onUpdateStageIdToNameMap = (stageIdToNameMap: { [key: string]: string }) => {
        this._stageIdToNameMap = stageIdToNameMap;
        if (this._itemList.testHistoryListItems.length) {
            this._createItemListToShow();
            this.emitChanged();
        }
    };

    private _createItemListToShow(): void {
        this._state.items = this._getNewItemsToInsert(this._itemList.testHistoryListItems);

        // Insert ShowMore item if there is possibility of more items.
        if (this._itemList.nextPageToken && this._itemList.nextPageToken.token) {
            this._state.items.push({
                itemkey: this._showMoreKey,
                itemName: Resources.ShowMoreText,
                nodeType: CommonTypes.TreeNodeType.showMore,
                depth: 0
            } as ITestInsightsTestHistoryListItem);
        }
    }

    private _getNewItemsToInsert(testHistoryListItems: ITestInsightsTestHistoryListItem[]): ITestInsightsTestHistoryListItem[] {
        return testHistoryListItems.map(i => {
            const item = { ...i };
            item.nodeType = i.nodeType ? i.nodeType : CommonTypes.TreeNodeType.leaf;
            item.depth = 0;
            item.duration = DurationFormatter.getDurationInAbbreviatedFormat(i.duration as number);
            item.durationAriaLabel = DurationFormatter.getDurationAriaLabel(i.duration as number);
            item.environmentRef.name =
                i.environmentRef && this._stageIdToNameMap && this._stageIdToNameMap[i.environmentRef.id.toString()]
                    ? this._stageIdToNameMap[i.environmentRef.id.toString()]
                    : StringFormattingUtility.getDeletedEnvironmentDefIdDisplayString(i.environmentRef.id.toString());

            return item;
        });
    }
}
